import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

// Public "my followers dropped" portal, reached from the emails that link
// viralgpt.com/refill. Email in, profiles out, scan one, claim the refill —
// the same /refill/scan the dashboard tab uses, so the 24-hour limit and the
// delivery path are identical.
const API = 'https://api.viralgpt.com';

const RefillPortal = () => {
  const L = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [claimed, setClaimed] = useState(false);

  const profiles = data?.profiles || [];
  const selectedProfile = profiles.find((p) => p.profileid === selectedProfileId);

  const handleLookup = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    setLoading(true);
    setError(null);
    setScan(null);
    setScanError(null);
    setClaimed(false);
    setSelectedProfileId('');
    try {
      const response = await fetch(`${API}/refill/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const body = await response.json();
      if (response.ok) {
        setData(body);
        // One profile is the common case — select it so there is nothing to pick.
        if ((body.profiles || []).length === 1) setSelectedProfileId(body.profiles[0].profileid);
      } else {
        setError(body.error || L.refundPage.lookupFailed);
      }
    } catch {
      setError(L.refundPage.requestError);
    } finally {
      setLoading(false);
    }
  };

  const runScan = async () => {
    if (!selectedProfile) return;
    setScanning(true);
    setScan(null);
    setScanError(null);
    setClaimed(false);
    try {
      const response = await fetch(`${API}/refill/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platform: selectedProfile.platform,
          username: selectedProfile.username,
          profileid: selectedProfile.profileid,
          mode: 'lookup',
        }),
      });
      const body = await response.json();
      // The endpoint answers 200 with an `error` field for its own refusals
      // (24-hour limit), so a bad status is not the only failure to handle.
      if (!response.ok || body.error) {
        setScanError(body.error || L.refillPortal.scanFailed);
        return;
      }
      setScan(body);
    } catch {
      setScanError(L.refillPortal.scanFailed);
    } finally {
      setScanning(false);
    }
  };

  const requestRefill = async () => {
    if (!selectedProfile) return;
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/refill/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platform: selectedProfile.platform,
          username: selectedProfile.username,
          profileid: selectedProfile.profileid,
          mode: 'request',
        }),
      });
      const body = await response.json();
      if (!response.ok || body.error) {
        setScanError(body.error || L.refillPortal.requestFailed);
        return;
      }
      setClaimed(true);
    } catch {
      setScanError(L.refillPortal.requestFailed);
    } finally {
      setSubmitting(false);
    }
  };

  const missing = scan ? parseInt(scan.missingFollowers, 10) || 0 : 0;

  // A one-time package buyer has no daily tracking to refill against, so the
  // scan answers with a delivery summary instead.
  //
  // Every figure below now comes from the server. It used to derive the start
  // from profiles.followers, which is rewritten on every profile verification —
  // so an account seen since delivery reported today's count as its starting
  // count, printed "up 1 follower" for someone who had gained 1,278, and worked
  // out a 1,199 shortfall against a customer whose order the panel shows as
  // fully delivered. The start now comes from the panel's start_count and the
  // shortfall with it; `eligible` carries the settling and churn guards, which
  // live on the server because the request path has to enforce them anyway.
  const summary = scan?.summary || null;
  const sStart = summary ? Number(summary.startFollowers) : null;
  const sNow = summary ? Number(summary.currentFollowers) || 0 : 0;
  const sPurchased = summary ? Number(summary.purchased) || 0 : 0;
  const sDelivered = summary ? Number(summary.delivered) || 0 : 0;
  const sPending = summary ? Number(summary.pending) || 0 : 0;
  const showStart = !!summary && Number.isFinite(sStart) && sStart > 0;
  const sGained = showStart ? sNow - sStart : 0;
  const sShortfall = summary ? Number(summary.missing) || 0 : 0;
  const sShowShortfall = !!summary?.eligible && sShortfall > 0;
  // Short by the arithmetic, but not yet claimable — the order is still filling
  // or the gap is inside normal churn. Saying "nothing is missing" here is what
  // made the card contradict itself.
  const sSettling = !sShowShortfall && sShortfall > 0;

  return (
    <>
      <Navbar />
      <div className="container py-5">
        <div className="text-center mb-5">
          <h1 className="text-instagram"><strong>{L.refillPortal.title}</strong></h1>
          <p className="mt-3">{L.refillPortal.intro}</p>
        </div>

        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card shadow-sm">
              <div className="card-body">
                <h5 className="card-title mb-4">{L.refillPortal.lookupTitle}</h5>

                <form onSubmit={handleLookup}>
                  <div className="mb-3">
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      placeholder={L.refundPage.emailPlaceholder}
                      required
                    />
                  </div>
                  <button type="submit" className="btn bg-instagram text-white w-100" disabled={loading}>
                    {loading ? L.common.loading : L.refillPortal.findAccounts}
                  </button>
                </form>

                {error && <div className="alert alert-danger mt-3">{error}</div>}

                {data && profiles.length === 0 && (
                  <div className="alert alert-info mt-4 mb-0">{L.refillPortal.noProfiles}</div>
                )}

                {profiles.length > 0 && (
                  <div className="mt-4">
                    <label htmlFor="refill-profile" className="form-label">{L.refillPortal.selectProfile}</label>
                    <select
                      id="refill-profile"
                      className="form-select"
                      value={selectedProfileId}
                      onChange={(e) => {
                        setSelectedProfileId(e.target.value);
                        setScan(null);
                        setScanError(null);
                        setClaimed(false);
                      }}
                    >
                      <option value="">{L.refillPortal.chooseProfile}</option>
                      {profiles.map((p) => (
                        <option key={p.profileid} value={p.profileid}>
                          {p.platform} — @{p.username}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      className="btn bg-instagram text-white w-100 mt-3"
                      onClick={runScan}
                      disabled={!selectedProfileId || scanning}
                    >
                      <i className={`fas ${scanning ? 'fa-spinner fa-spin' : 'fa-search'} me-2`}></i>
                      {scanning ? L.refillPortal.scanning : L.refillPortal.scanProfile}
                    </button>
                  </div>
                )}

                {scanError && <div className="alert alert-warning mt-3 mb-0">{scanError}</div>}

                {summary && (
                  <div className="mt-4 p-3" style={{ border: '1px solid #dee2e6', borderRadius: 8 }}>
                    <div className="d-flex align-items-center gap-3 mb-3">
                      {summary.photo && (
                        <img
                          src={summary.photo}
                          alt={summary.username}
                          className="rounded-circle"
                          style={{ width: 40, height: 40, objectFit: 'cover' }}
                        />
                      )}
                      <h6 className="mb-0">
                        {sPurchased > 0 ? L.refillPortal.deliveredTitle : L.refillPortal.summaryTitle}
                      </h6>
                    </div>

                    <div style={{ fontSize: '0.9rem' }}>
                      {showStart && (
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-muted">{L.refillPortal.startedWith}</span>
                          <strong>{sStart.toLocaleString()}</strong>
                        </div>
                      )}
                      {sPurchased > 0 && (
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-muted">{L.refillPortal.youPurchased}</span>
                          <strong className="text-success">+{sPurchased.toLocaleString()}</strong>
                        </div>
                      )}
                      {sPending > 0 && (
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-muted">{L.refillPortal.stillDelivering}</span>
                          <strong>{sPending.toLocaleString()}</strong>
                        </div>
                      )}
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted">{L.refillPortal.rightNow}</span>
                        <strong>{sNow.toLocaleString()}</strong>
                      </div>
                    </div>

                    <div
                      className={`alert mt-3 mb-0 ${sSettling ? 'alert-info' : 'alert-success'}`}
                      style={{ fontSize: '0.9rem', display: sShowShortfall ? 'none' : undefined }}
                    >
                      <i className={`fas ${sSettling ? 'fa-hourglass-half' : 'fa-circle-check'} me-2`}></i>
                      {sPending > 0
                        ? L.refillPortal.inFlightNote(sPending.toLocaleString())
                        : (sSettling
                          ? L.refillPortal.settlingNote(sShortfall.toLocaleString())
                          : (showStart && sDelivered > 0
                            ? L.refillPortal.deliveredNote(sDelivered.toLocaleString(), sGained.toLocaleString())
                            : (sDelivered > 0
                              ? L.refillPortal.deliveredNoteShort(sDelivered.toLocaleString(), sNow.toLocaleString())
                              : L.refillPortal.summaryNote(sNow.toLocaleString()))))}
                    </div>

                    {sPending === 0 && sDelivered > 0 && !showStart && !sShowShortfall && (
                      <div className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>
                        {L.refillPortal.helpNote}{' '}
                        <a href="mailto:support@viralgpt.com" className="link-blue">support@viralgpt.com</a>
                      </div>
                    )}

                    {sShowShortfall && !claimed && (
                      <div className="alert alert-warning mt-3 mb-0" style={{ fontSize: '0.88rem' }}>
                        <i className="fas fa-life-ring me-2"></i>
                        {L.refillPortal.shortfallNote(sShortfall.toLocaleString())}
                        <button
                          type="button"
                          className="btn btn-success w-100 mt-3"
                          onClick={requestRefill}
                          disabled={submitting}
                        >
                          <i className={`fas ${submitting ? 'fa-spinner fa-spin' : 'fa-sync-alt'} me-2`}></i>
                          {submitting ? L.refillPortal.submitting : L.refillPortal.requestRefill(sShortfall.toLocaleString())}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {scan && !summary && !claimed && (
                  <div className="mt-4 p-3" style={{ border: '1px solid #dee2e6', borderRadius: 8 }}>
                    <div className="d-flex align-items-center gap-3 mb-3">
                      {scan.photo && (
                        <img
                          src={scan.photo}
                          alt={selectedProfile?.username}
                          className="rounded-circle"
                          style={{ width: 40, height: 40, objectFit: 'cover' }}
                        />
                      )}
                      <h6 className="mb-0">{L.refillPortal.scanResults}</h6>
                    </div>

                    {missing > 0 ? (
                      <>
                        <div style={{ fontSize: '0.9rem' }}>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted">{L.refillPortal.expected}</span>
                            <strong>{(scan.expectedFollowers || 0).toLocaleString()}</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted">{L.refillPortal.current}</span>
                            <strong>{(scan.currentFollowers || 0).toLocaleString()}</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted">{L.refillPortal.missing}</span>
                            <strong className="text-danger">{missing.toLocaleString()}</strong>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-success w-100 mt-3"
                          onClick={requestRefill}
                          disabled={submitting}
                        >
                          <i className={`fas ${submitting ? 'fa-spinner fa-spin' : 'fa-sync-alt'} me-2`}></i>
                          {submitting ? L.refillPortal.submitting : L.refillPortal.requestRefill(missing.toLocaleString())}
                        </button>
                      </>
                    ) : (
                      <div className="alert alert-success mb-0" style={{ fontSize: '0.9rem' }}>
                        <i className="fas fa-check-circle me-2"></i>
                        {L.refillPortal.noneMissing((scan.currentFollowers || 0).toLocaleString())}
                      </div>
                    )}
                  </div>
                )}

                {claimed && (
                  <div className="alert alert-success mt-4 mb-0">
                    <i className="fas fa-check-circle me-2"></i>
                    {L.refillPortal.claimed}
                  </div>
                )}

                <div className="mt-4 text-muted small">
                  <p className="mb-1">
                    <i className="fa-solid fa-info-circle me-1"></i>
                    {L.refillPortal.limitNote}
                  </p>
                  <p className="mb-0">{L.refundPage.immediateAssistance}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default RefillPortal;
